import React, { useState } from 'react'
import { Mail, Lock, User, Eye, EyeOff } from 'lucide-react'
import { authService } from '../../services/auth'

interface RegisterFormProps {
  onSuccess?: () => void
  onLoginClick?: () => void
}

export function RegisterForm({ onSuccess, onLoginClick }: RegisterFormProps): React.ReactElement {
  const [email, setEmail] = useState('')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    setLoading(true)

    try {
      const response = await authService.register(email, username, password)
      authService.setToken(response.token)
      authService.setUser(response.user)
      onSuccess?.()
    } catch (err: any) {
      setError(err.response?.data?.error || 'Registration failed')
    } finally {
      setLoading(false)
    }
  }

  const isLoading = loading

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-yellow-50 flex items-center justify-center p-4">
      <form
        onSubmit={handleSubmit}
        className="max-w-md w-full bg-gradient-to-br from-green-100 to-cyan-100 rounded-3xl shadow-xl border-4 border-green-200 p-10 space-y-6"
      >
        {/* Logo with Emoji */}
        <div className="flex justify-center mb-6">
          <div className="w-20 h-20 bg-gradient-to-br from-green-300 to-cyan-300 rounded-3xl flex items-center justify-center text-white font-bold text-5xl shadow-lg">
            🎉
          </div>
        </div>

        {/* Heading */}
        <div className="text-center space-y-2">
          <h1 className="text-4xl font-black text-green-700">Join the Kingdom!</h1>
          <p className="text-lg text-cyan-600 font-bold">Create your magical account! ✨</p>
        </div>

        {/* Email Field */}
        <div className="space-y-2">
          <label className="block text-sm font-bold text-green-700">Email</label>
          <div className="flex items-center bg-white border-3 border-cyan-200 rounded-2xl px-4 py-4 focus-within:border-cyan-400 focus-within:ring-2 focus-within:ring-cyan-200">
            <Mail className="w-6 h-6 text-cyan-400 mr-3" />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              className="flex-1 outline-none text-gray-900 bg-transparent font-semibold"
              required
            />
          </div>
        </div>

        {/* Username Field */}
        <div className="space-y-2">
          <label className="block text-sm font-bold text-green-700">Username</label>
          <div className="flex items-center bg-white border-3 border-cyan-200 rounded-2xl px-4 py-4 focus-within:border-cyan-400 focus-within:ring-2 focus-within:ring-cyan-200">
            <User className="w-6 h-6 text-cyan-400 mr-3" />
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Your username (3-20 chars)"
              className="flex-1 outline-none text-gray-900 bg-transparent font-semibold"
              minLength={3}
              maxLength={20}
              required
            />
          </div>
        </div>

        {/* Password Field */}
        <div className="space-y-2">
          <label className="block text-sm font-bold text-green-700">Password</label>
          <div className="flex items-center bg-white border-3 border-cyan-200 rounded-2xl px-4 py-4 focus-within:border-cyan-400 focus-within:ring-2 focus-within:ring-cyan-200">
            <Lock className="w-6 h-6 text-cyan-400 mr-3" />
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 6 characters"
              className="flex-1 outline-none text-gray-900 bg-transparent font-semibold"
              minLength={6}
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="text-cyan-400 hover:text-cyan-500 transition"
            >
              {showPassword ? <EyeOff className="w-6 h-6" /> : <Eye className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Confirm Password Field */}
        <div className="space-y-2">
          <label className="block text-sm font-bold text-green-700">Confirm Password</label>
          <div className="flex items-center bg-white border-3 border-cyan-200 rounded-2xl px-4 py-4 focus-within:border-cyan-400 focus-within:ring-2 focus-within:ring-cyan-200">
            <Lock className="w-6 h-6 text-cyan-400 mr-3" />
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm password"
              className="flex-1 outline-none text-gray-900 bg-transparent font-semibold"
              minLength={6}
              required
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="text-cyan-400 hover:text-cyan-500 transition"
            >
              {showConfirmPassword ? <EyeOff className="w-6 h-6" /> : <Eye className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-gradient-to-r from-red-100 to-pink-100 border-3 border-red-300 rounded-2xl px-4 py-3">
            <p className="text-red-700 font-bold text-sm">⚠️ {error}</p>
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isLoading}
          className="w-full py-4 bg-gradient-to-r from-green-400 to-cyan-400 text-white font-black text-lg rounded-2xl hover:shadow-xl transition disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105"
        >
          {isLoading ? '⏳ Creating account...' : '🎊 Create Account'}
        </button>

        {/* Login Link */}
        <div className="text-center pt-4 border-t-3 border-cyan-300">
          <p className="text-green-700 font-bold text-lg">
            Already have an account?{' '}
            <button
              type="button"
              onClick={onLoginClick}
              className="text-cyan-600 hover:text-cyan-700 font-black underline"
            >
              Sign in! 👋
            </button>
          </p>
        </div>
      </form>
    </div>
  )
}
